<script setup lang="ts">
/**
 * Zoom for a canvas, a floor plan, a page preview: − / percentage / + and
 * optionally „Einpassen“ and a slider. `v-model` is the factor (1 = 100 %),
 * kept within `min`–`max`; − and + move by `step`, or multiply and divide by
 * `factor` (1.25: 100 → 125 → 156 %) — the logic is `nxZoomStep`, exported
 * for a caller's own keys and gestures. A click on the percentage goes back
 * to 100 %. `fit` is emitted; when the caller knows the fitting factor up
 * front (`fitValue`, e.g. 1 for a floor plan drawn to fit), the button also
 * sets it and rests disabled while it is set. `fitIconOnly` shows the fit
 * button as an icon, for a control laid over a canvas.
 *
 * With `shortcuts`, Ctrl/⌘ + „+“, „−“ and „0“ zoom anywhere on the page
 * (and the browser's own page zoom is kept out of it).
 */
import Maximize2 from 'lucide-vue-next/dist/esm/icons/maximize-2.js'; import Minus from 'lucide-vue-next/dist/esm/icons/minus.js'; import Plus from 'lucide-vue-next/dist/esm/icons/plus.js';
import { computed, inject, onBeforeUnmount, onMounted, watch } from 'vue';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { NxSlider } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import { NX_ZOOM_DEFAULTS, nxZoomClamp, nxZoomStep } from './zoomStep';

const props = withDefaults(
  defineProps<{
    min?: number;
    max?: number;
    step?: number;
    /** Multiplicative step instead of `step` (> 1, e.g. 1.25). The slider stays linear. */
    factor?: number;
    /** The factor at which the content fits: the fit button sets it and is disabled there. */
    fitValue?: number;
    /** The fit button as an icon (its label as tooltip and name). */
    fitIconOnly?: boolean;
    /** Name of the group for screen readers, read before the percentage. */
    label?: string;
    /** Shows a slider between − and +. */
    slider?: boolean;
    /** Shows the „Einpassen“ button (emits `fit`). */
    fittable?: boolean;
    /** Ctrl/⌘ + „+“ / „−“ / „0“ anywhere on the page. */
    shortcuts?: boolean;
    zoomInLabel?: string;
    zoomOutLabel?: string;
    resetLabel?: string;
    fitLabel?: string;
    disabled?: boolean;
    locale?: string;
  }>(),
  {
    min: NX_ZOOM_DEFAULTS.min,
    max: NX_ZOOM_DEFAULTS.max,
    step: NX_ZOOM_DEFAULTS.step,
    factor: undefined,
    fitValue: undefined,
    fitIconOnly: false,
    label: undefined,
    slider: false,
    fittable: false,
    shortcuts: false,
    zoomInLabel: 'Vergrößern',
    zoomOutLabel: 'Verkleinern',
    resetLabel: 'Auf 100 % zurücksetzen',
    fitLabel: 'Einpassen',
    disabled: false,
    locale: undefined,
  }
);

const model = defineModel<number>({ required: true });
const emit = defineEmits<{ fit: [] }>();

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const percentFormat = computed(
  () => new Intl.NumberFormat(locale.value, { style: 'percent', maximumFractionDigits: 0 })
);

const stepOptions = computed(() => ({
  min: props.min,
  max: props.max,
  step: props.step,
  factor: props.factor,
}));
function set(value: number) {
  if (props.disabled) return;
  model.value = nxZoomClamp(value, stepOptions.value);
}
function zoom(direction: 1 | -1) {
  if (props.disabled) return;
  model.value = nxZoomStep(model.value, direction, stepOptions.value);
}
function fit() {
  if (props.fitValue !== undefined) set(props.fitValue);
  emit('fit');
}

const percent = computed(() => percentFormat.value.format(model.value));
const atMin = computed(() => model.value <= props.min + 1e-9);
const atMax = computed(() => model.value >= props.max - 1e-9);
const atFit = computed(
  () => props.fitValue !== undefined && Math.abs(model.value - props.fitValue) < 1e-9
);
const groupLabel = computed(() =>
  props.label ? `${props.label}, ${percent.value}` : percent.value
);

const sliderValue = computed({
  get: () => Math.round(model.value * 100),
  set: (v: number) => set(v / 100),
});

function onGlobalKey(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || props.disabled) return;
  if (event.key === '+' || event.key === '=') zoom(1);
  else if (event.key === '-' || event.key === '_') zoom(-1);
  else if (event.key === '0') set(1);
  else return;
  event.preventDefault();
}

function bind(on: boolean) {
  if (typeof window === 'undefined') return;
  if (on) window.addEventListener('keydown', onGlobalKey);
  else window.removeEventListener('keydown', onGlobalKey);
}
onMounted(() => bind(props.shortcuts));
watch(
  () => props.shortcuts,
  (on) => {
    bind(false);
    bind(on);
  }
);
onBeforeUnmount(() => bind(false));
</script>

<template>
  <div
    class="nx-zoom-control"
    :class="{ 'is-disabled': props.disabled }"
    role="group"
    :aria-label="groupLabel"
  >
    <NxIconButton
      height="s"
      :title="props.zoomOutLabel"
      :aria-label="props.zoomOutLabel"
      :disabled="props.disabled || atMin"
      data-testid="nx-zoom-out"
      @click="zoom(-1)"
    >
      <Minus class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
    <NxSlider
      v-if="props.slider"
      v-model="sliderValue"
      class="nx-zoom-control__slider"
      :min="Math.round(props.min * 100)"
      :max="Math.round(props.max * 100)"
      :step="Math.max(1, Math.round(props.step * 100))"
      :disabled="props.disabled"
      :aria-label="percent"
    />
    <button
      type="button"
      class="nx-zoom-control__value"
      :title="props.resetLabel"
      :aria-label="`${percent}, ${props.resetLabel}`"
      :disabled="props.disabled"
      data-testid="nx-zoom-value"
      @click="set(1)"
    >
      <span aria-live="polite">{{ percent }}</span>
    </button>
    <NxIconButton
      height="s"
      :title="props.zoomInLabel"
      :aria-label="props.zoomInLabel"
      :disabled="props.disabled || atMax"
      data-testid="nx-zoom-in"
      @click="zoom(1)"
    >
      <Plus class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
    <template v-if="props.fittable">
      <span class="nx-zoom-control__divider" aria-hidden="true" />
      <NxIconButton
        v-if="props.fitIconOnly"
        height="s"
        :title="props.fitLabel"
        :aria-label="props.fitLabel"
        :disabled="props.disabled || atFit"
        data-testid="nx-zoom-fit"
        @click="fit"
      >
        <Maximize2 class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <NxButton
        v-else
        height="s"
        variant="ghost"
        :disabled="props.disabled || atFit"
        data-testid="nx-zoom-fit"
        @click="fit"
      >
        <Maximize2 class="nxt-icon-sm" aria-hidden="true" />
        {{ props.fitLabel }}
      </NxButton>
    </template>
  </div>
</template>

<style scoped>
.nx-zoom-control {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-zoom-control__slider {
  width: calc(var(--nxt-size-unit) * 7);
}
.nx-zoom-control__value {
  min-width: calc(var(--nxt-size-unit) * 3.25);
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.nx-zoom-control__value:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
}
.nx-zoom-control__value:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-zoom-control__value:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.nx-zoom-control__divider {
  width: var(--nxt-hairline);
  height: calc(var(--nxt-control-size-sm) * 0.6);
  margin-inline: var(--nxt-space-2xs);
  background: var(--nxt-border);
}
</style>
